import { useCallback, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';

/** `idle` → `starting` (permission prompt) → `ready` (live preview), or `error`. */
export type CameraCaptureStatus = 'idle' | 'starting' | 'ready' | 'error';

/** JPEG rather than PNG: a camera frame as PNG runs into the 20 MB upload limit. */
const CAPTURE_MIME_TYPE = 'image/jpeg';
const CAPTURE_QUALITY = 0.92;
const CAPTURE_IDEAL_WIDTH = 1920;
const CAPTURE_IDEAL_HEIGHT = 1080;

/**
 * Whether the device can be asked for a camera at all.
 *
 * `mediaDevices` is undefined outside a secure context, so this is false over
 * plain HTTP as well as on browsers without the API — in both cases the button
 * is hidden rather than offered and left to fail.
 */
export const isCameraCaptureSupported = (): boolean =>
  typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

/**
 * Whether to hand the photo over to the device's own camera app instead of
 * previewing it in the page.
 *
 * Phones and tablets get the native camera: it shoots at the sensor's full
 * resolution with zoom, flash and tap-to-focus, none of which a video stream
 * offers. A coarse primary pointer is the signal — viewport width would call a
 * phone in landscape a desktop, and a narrow browser window a phone.
 */
export const isNativeCameraPreferred = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

const buildPhotoName = (): string => `photo-${format(new Date(), 'yyyyMMdd-HHmmss')}.jpg`;

interface UseCameraCaptureArgs {
  onCapture: (file: File) => void;
}

/**
 * Live camera preview and a still frame from it, as a `File`.
 *
 * The frame is drawn onto a canvas instead of being taken with `ImageCapture`
 * (which is what the JSF page uses): that API is Chrome-only, while a canvas
 * works everywhere and lets the still come out as JPEG.
 *
 * The stream is stopped on unmount, after a capture and on `stop()` — a camera
 * left running keeps the device's indicator on, which users read as the app
 * watching them.
 */
export const useCameraCapture = ({ onCapture }: UseCameraCaptureArgs) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<CameraCaptureStatus>('idle');

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setStatus('idle');
  }, []);

  const start = useCallback(async () => {
    if (!isCameraCaptureSupported()) {
      setStatus('error');
      return;
    }

    setStatus('starting');
    try {
      // The rear camera is the one pointed at the cargo, and full HD keeps a
      // waybill readable; both are `ideal` rather than `exact`, so a laptop with
      // one low-resolution front camera still works.
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: CAPTURE_IDEAL_WIDTH },
          height: { ideal: CAPTURE_IDEAL_HEIGHT }
        },
        audio: false
      });
      setStatus('ready');
    } catch {
      streamRef.current = null;
      setStatus('error');
    }
  }, []);

  // Attached here rather than in `start`: the <video> is only mounted once the
  // status says there is something to show, so at that point the ref is still null.
  useEffect(() => {
    if (status === 'ready' && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [status]);

  useEffect(() => stop, [stop]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (status !== 'ready' || !video?.videoWidth || !video.videoHeight) {
      setStatus('error');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      setStatus('error');
      return;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setStatus('error');
          return;
        }
        onCapture(new File([blob], buildPhotoName(), { type: CAPTURE_MIME_TYPE }));
        stop();
      },
      CAPTURE_MIME_TYPE,
      CAPTURE_QUALITY
    );
  }, [status, onCapture, stop]);

  return { status, videoRef, start, capture, stop };
};
